import { Menu, Transition } from "@headlessui/react";
import { Fragment } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useUserDataStore } from "../../stores/user";
import ImgPlaceholder from "../../assets/ImgPlaceholder.svg";
import SettingsIcon from "../Icons/SettingsIcon";
import LogoutIcon from "../Icons/LogoutIcon";
import { clearAccessToken } from "../../lib/session";

const AdminProfileDropdown = () => {
  const { userData, reset } = useUserDataStore();
  const navigate = useNavigate();
  const isSales = userData?.role === "sales";
  const profilePath = isSales ? "/sales/dashboard/profile" : "/dashboard/profile";
  // The API has no admin self-read/update routes worth surfacing yet, so the
  // profile entry is sales-only for now. The /dashboard/profile route stays.
  const showProfileLink = isSales;

  const handleLogout = () => {
    clearAccessToken();
    reset();
    navigate(isSales ? "/login/sales" : "/login/admin", { replace: true });
  };

  return (
    <Menu as="div" className="relative inline-block text-left z-[100]">
      <Menu.Button className="flex items-center gap-3 rounded-2xl border border-black/5 bg-white px-2.5 py-2 shadow-sm transition hover:border-secondary/30 hover:shadow-md focus:outline-none focus-visible:ring-2 focus-visible:ring-secondary/30">
        <div className="size-10">
          <img
            src={userData?.img_url ?? ImgPlaceholder}
            className="h-full w-full rounded-full object-cover object-[75%_25%] ring-2 ring-secondary/10"
            alt=""
          />
        </div>
        <div className="hidden text-left sm:block">
          <h1 className="mb-0.5 text-sm font-bold capitalize text-textPrimary">
            {userData?.firstName}
          </h1>
          <p className="text-xs font-semibold text-lightGray">
            {isSales ? "Sales" : "Admin"}
          </p>
        </div>
      </Menu.Button>
      <Transition
        as={Fragment}
        enter="transition ease-out duration-100"
        enterFrom="transform opacity-0 scale-95"
        enterTo="transform opacity-100 scale-100"
        leave="transition ease-in duration-75"
        leaveFrom="transform opacity-100 scale-100"
        leaveTo="transform opacity-0 scale-95"
      >
        <Menu.Items className="absolute right-0 top-14 z-[100] min-w-[300px] origin-top-right overflow-hidden rounded-2xl border border-black/10 bg-white text-left shadow-[0_24px_70px_rgba(17,24,22,0.18)] ring-1 ring-black/5 focus:outline-none">
          <div className="flex items-center gap-3 pb-4 pt-5 px-4">
            <img
              src={userData?.img_url ?? ImgPlaceholder}
              className="size-14 rounded-full border border-black/10 object-cover ring-4 ring-secondary/10"
              alt=""
            />
            <div className="w-full">
              <h1 className="mb-0.5 font-semibold text-textPrimary">
                {userData?.firstName} {userData?.lastName}
              </h1>
              <p className="max-w-[200px] truncate text-xs text-lightGray">
                {userData?.email}
              </p>
            </div>
          </div>
          {showProfileLink && (
            <div className="px-4 py-5">
              <Menu.Item>
                <Link
                  to={profilePath}
                  className="flex cursor-pointer items-center gap-2 rounded-xl px-2 py-2 text-sm font-semibold text-textPrimary transition hover:bg-secondary/10"
                >
                  <SettingsIcon />
                  <p>My Profile</p>
                </Link>
              </Menu.Item>
            </div>
          )}
          <div className="px-4 py-5">
            <Menu.Item>
              <button
                onClick={handleLogout}
                className="flex w-full items-center gap-2 rounded-xl px-2 py-2 text-sm font-semibold text-red-600 transition hover:bg-red-50"
              >
                <LogoutIcon />
                <p>Log Out</p>
              </button>
            </Menu.Item>
          </div>
        </Menu.Items>
      </Transition>
    </Menu>
  );
};

export default AdminProfileDropdown;
